var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { ModalContent } from '@amzn/atoz-web/Modal';
import { Button, ButtonVariant } from '@amzn/atoz-web/Button';
import { LoadingButton } from '../../shared';
import { SpinnerColorScheme } from '@amzn/atoz-web/Spinner';
import { Text } from '@amzn/atoz-web/Text';
import { useTranslation } from 'react-i18next';
import { MessageBanner, MessageBannerType } from '@amzn/atoz-web/MessageBanner';
import { View } from '@amzn/atoz-web/View';
import { Row } from '@amzn/atoz-web/Row';
import { DateTime } from 'luxon';
import { timeDifferenceWithDurationString } from '@amzn/atoz-shiftapp-utilities';
import { Col } from '@amzn/atoz-web/Col';
import { logger, METRIC_NAME } from '@amzn/atoz-shiftapp-metrics';
import { useLegacyCancelSwapRequestModalContentData } from './utils';
export var LegacyCancelSwapRequestModalContent = function (_a) {
    var close = _a.close, scheduleShift = _a.scheduleShift, shiftsForDay = _a.shiftsForDay, setModalContent = _a.setModalContent, setModalError = _a.setModalError, siteSwapConfiguration = _a.siteSwapConfiguration, swaps = _a.swaps;
    var t = useTranslation().t;
    var _b = useLegacyCancelSwapRequestModalContentData({
        setModalContent: setModalContent,
        setModalError: setModalError,
        scheduleShift: scheduleShift,
        siteSwapConfiguration: siteSwapConfiguration,
        swaps: swaps
    }), isSubmitting = _b.isSubmitting, onSubmit = _b.onSubmit, siteId = _b.siteId;
    var hasMultipleShifts = shiftsForDay.length > 1;
    var sharedModalContentProps = {
        titleText: t('shift_management_features_CancelSwapRequestModal_title'),
        buttons: [
            React.createElement(Button, { key: "cancelButton", dataTestId: "LegacyCancelSwapRequestModalContent_cancelButton", onClick: close, variant: ButtonVariant.Secondary }, t('shift_management_features_CancelSwapRequestModal_closeButton')),
            React.createElement(LoadingButton, { loading: isSubmitting, key: "confirmButton", ariaLabel: t('shift_management_features_CancelSwapRequestModal_confirmButton2'), altText: t('shift_management_common_button', {
                    label: t('shift_management_features_CancelSwapRequestModal_confirmButton2')
                }), dataTestId: "LegacyCancelSwapRequestModalContent_confirmButton", onClick: onSubmit, spinnerColor: SpinnerColorScheme.Inverted, buttonVariant: ButtonVariant.Primary, buttonText: t('shift_management_features_CancelSwapRequestModal_confirmButton2') })
        ],
        onCloseButtonClick: close,
        closeButtonAltText: t('shift_management_common_closeModalButton')
    };
    // shiftsForDay should always contain at least one element
    // due to how a user navigates to this modal
    // but will handle the theoretically-impossible scenario where it is of length 0
    // TODO: Move this logger and length handling out to the feature component once legacy is deprecated
    if (shiftsForDay.length === 0) {
        logger.emitCountMetric({
            metricName: METRIC_NAME.DATA_ERROR_SWAP_SHIFTS_FOR_DAY_EMPTY,
            dimensions: {}
        });
        logger.error('LegacyCancelSwapRequestModalContent.web received an empty shiftsForDay');
        return (React.createElement(ModalContent, __assign({ dataTestId: "LegacyCancelSwapRequestModalContent_Error" }, sharedModalContentProps),
            React.createElement(Col, { gridGap: "S300" },
                React.createElement(Text, null, t('shift_management_features_CancelSwapRequestModal_body_generic')))));
    }
    var dateOfShifts = DateTime.fromISO(shiftsForDay[0].shift.timeRange.start);
    var swapFromDate = dateOfShifts.toLocaleString({
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
    return (React.createElement(ModalContent, __assign({ dataTestId: "LegacyCancelSwapRequestModalContent" }, sharedModalContentProps),
        React.createElement(Col, { gridGap: "S300" },
            hasMultipleShifts && (React.createElement(MessageBanner, { dataTestId: "LegacyCancelSwapRequestModalContent_multipleShiftBanner", type: MessageBannerType.Warning },
                React.createElement(Text, null, t('shift_management_features_CancelSwapRequestModal_multipleShiftBanner', {
                    swapFromDate: swapFromDate
                })))),
            React.createElement(Text, { fontWeight: "medium", role: "heading", "aria-level": 3 }, t('shift_management_features_LegacyCancelSwapRequestModal_subtitle')),
            React.createElement(Text, { dataTestId: "originalShiftString", lineHeight: 0.5 }, swapFromDate),
            React.createElement(View, { backgroundColor: "neutral05", padding: ['S300', 'S400'], margin: { top: 'S100' } },
                React.createElement(View, { role: "list" }, shiftsForDay.map(function (_a) {
                    var shift = _a.shift;
                    return (React.createElement(View, { key: "OriginalShift-".concat(shift.timeRange.start, "-").concat(shift.timeRange.end), dataTestId: "OriginalShift-".concat(shift.timeRange.start, "-").concat(shift.timeRange.end), padding: { top: 'S200', bottom: 'S200' }, role: "listitem" },
                        React.createElement(Text, { dataTestId: "originalShiftDurationString", fontWeight: "medium" }, timeDifferenceWithDurationString(DateTime.fromISO(shift.timeRange.start), DateTime.fromISO(shift.timeRange.end)))));
                })),
                siteId && (React.createElement(Row, { padding: { top: 'S100' } },
                    React.createElement(Text, null, siteId)))),
            React.createElement(Text, null, hasMultipleShifts
                ? t('shift_management_features_LegacyCancelSwapRequestModal_body_multipleShift')
                : t('shift_management_features_LegacyCancelSwapRequestModal_body')))));
};
